/* eslint-disable no-param-reassign */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import getWalletTransactions, {
	GetWalletTransactionResponse,
	GetWalletTransactionParams,
} from 'api/clients/getWalletTransactions'
import {
	ClientWallet,
	ClientWalletTransaction,
	clientWalletTransactionData,
} from 'types'

/**
 * Type for the slice of wallet transaction in the store
 */
export const CLIENT_WALLET_INITIAL_PAGE_SIZE = 10
export interface walletTransactionState {
	transactions: clientWalletTransactionData | null
	records: ClientWalletTransaction[] | []
	totalCount: number
	totalPages: number
	currentPage: number
	wallet: ClientWallet | null
	loading: boolean
	pageSize: number
}

/**
 * initial state of the slice wallet transaction
 */

const initialState: walletTransactionState = {
	transactions: null,
	records: [],
	totalCount: 0,
	totalPages: 0,
	currentPage: 1,
	wallet: null,
	loading: false,
	pageSize: CLIENT_WALLET_INITIAL_PAGE_SIZE,
}

/**
 * Action for getting clients
 *
 * Tries to get wallet transaction from the API and returns the result in the payload
 *
 * @param {GetWalletTransactionParams} options - Options for the API call
 * @returns {Promise<GetWalletTransactionResponse>} - Response object returned from the server
 * @throws {Error} - Throws an error if the API call fails
 */

export const getWalletTransactionsAction = createAsyncThunk<
	GetWalletTransactionResponse,
	GetWalletTransactionParams
>(
	'walletTransaction/getWalletTransactionsAction',
	async (options, { rejectWithValue }) => {
		try {
			const response = await getWalletTransactions(options)
			return response
		} catch (error: any) {
			return rejectWithValue(error.message)
		}
	}
)

export const walletTransactionSlice = createSlice({
	name: 'walletTransaction',
	initialState,
	reducers: {
		clearWalletTransaction: () => {
			return initialState
		},
		setPageSize: (state, action: PayloadAction<number>) => {
			state.pageSize = action.payload
		},
	},
	extraReducers: builder => {
		builder.addCase(getWalletTransactionsAction.pending, state => {
			state.loading = true
		})
		builder.addCase(getWalletTransactionsAction.fulfilled, (state, action) => {
			state.transactions = action.payload.transactions
			state.records = action.payload.transactions?.records
			state.totalCount = action.payload.transactions?.totalCount
			state.totalPages = action.payload.transactions?.totalPages
			state.currentPage = action.payload.transactions?.currentPage
			state.wallet = action.payload.wallet
			state.loading = false
		})
		builder.addCase(getWalletTransactionsAction.rejected, state => {
			state.loading = false
		})
	},
})

export const { clearWalletTransaction, setPageSize } =
	walletTransactionSlice.actions

export default walletTransactionSlice.reducer
